import React, { useEffect, useMemo, useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BasicInfoStep = ({ formData, onChange, errors, venueOptions, onVenueSearch }) => {
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [venueSearchResults, setVenueSearchResults] = useState([]);
  const [venueSearchOpen, setVenueSearchOpen] = useState(false);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);
  const [venueSearchError, setVenueSearchError] = useState('');

  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [imageError, setImageError] = useState('');

  const venueOptionsForSelect = useMemo(() => {
    return Array.isArray(venueOptions) ? venueOptions : [];
  }, [venueOptions]);

  const levelOptions = [
    { value: 'beginner', label: '초급 (Beginner)' },
    { value: 'intermediate', label: '중급 (Intermediate)' },
    { value: 'advanced', label: '상급 (Advanced)' },
    { value: 'all', label: '전체 레벨 (All Levels)' }
  ];

  const handleChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const toDatetimeLocalValue = (date, time) => {
    const d = String(date || '').trim();
    const t = String(time || '').trim();
    if (!d || !t) return '';
    return `${d}T${t}`;
  };

  const splitDatetimeLocalValue = (value) => {
    const v = String(value || '').trim();
    if (!v || !v.includes('T')) return { date: '', time: '' };
    const [d, t] = v.split('T');
    return { date: String(d || '').trim(), time: String(t || '').trim() };
  };

  const addHoursToLocalDatetime = (date, time, hours) => {
    const d = String(date || '').trim();
    const t = String(time || '').trim();
    if (!d || !t) return { date: '', time: '' };

    const base = new Date(`${d}T${t}`);
    if (Number.isNaN(base.getTime())) return { date: '', time: '' };

    base.setHours(base.getHours() + (Number(hours) || 0));

    const yyyy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, '0');
    const dd = String(base.getDate()).padStart(2, '0');
    const hh = String(base.getHours()).padStart(2, '0');
    const min = String(base.getMinutes()).padStart(2, '0');

    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
  };

  useEffect(() => {
    if (formData?.sport === 'pickleball') return;
    onChange({ ...formData, sport: 'pickleball' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.sport]);

  const compressImageToUnder1MB = async (file) => {
    const MAX_BYTES = 1024 * 1024;
    if (!file || !(file instanceof File)) return file;
    if (!String(file?.type || '').startsWith('image/')) return file;
    if (file.size <= MAX_BYTES) return file;

    const blobUrl = URL.createObjectURL(file);
    try {
      let bitmap;
      try {
        bitmap = await createImageBitmap(file);
      } catch {
        const img = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = () => reject(new Error('Failed to load image'));
          el.src = blobUrl;
        });
        bitmap = img;
      }

      const origWidth = bitmap?.width || bitmap?.naturalWidth || 0;
      const origHeight = bitmap?.height || bitmap?.naturalHeight || 0;
      if (!origWidth || !origHeight) return file;

      const maxSide = 1600;
      const scale = Math.min(1, maxSide / Math.max(origWidth, origHeight));
      const targetWidth = Math.max(1, Math.round(origWidth * scale));
      const targetHeight = Math.max(1, Math.round(origHeight * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

      const toBlob = (type, quality) =>
        new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), type, quality);
        });

      const candidates = ['image/webp', 'image/jpeg'];
      for (const type of candidates) {
        let quality = 0.82;
        let out = await toBlob(type, quality);
        while (out && out.size > MAX_BYTES && quality > 0.35) {
          quality -= 0.08;
          out = await toBlob(type, quality);
        }
        if (out && out.size <= MAX_BYTES) {
          const ext = type === 'image/webp' ? 'webp' : 'jpg';
          return new File([out], `image.${ext}`, { type });
        }
      }

      const fallback = await toBlob('image/jpeg', 0.6);
      if (fallback) {
        return new File([fallback], 'image.jpg', { type: 'image/jpeg' });
      }
      return file;
    } finally {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {
        // ignore
      }
    }
  };

  const getFileExt = (file) => {
    const type = String(file?.type || '').toLowerCase();
    if (type === 'image/webp') return 'webp';
    if (type === 'image/png') return 'png';
    if (type === 'image/jpeg' || type === 'image/jpg') return 'jpg';
    if (type === 'image/gif') return 'gif';
    const raw = String(file?.name || '').split('.').pop();
    const ext = String(raw || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return ext || 'jpg';
  };

  const uuid = () => {
    return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const handleSelectImages = async (e) => {
    const files = Array.from(e?.target?.files || []).filter((f) => f instanceof File);
    e.target.value = '';
    if (files.length === 0) return;

    const existingUrls = Array.isArray(formData?.imageUrls) ? formData.imageUrls : [];
    const existingDrafts = Array.isArray(formData?.imageDrafts) ? formData.imageDrafts : [];
    const existingCount = existingUrls.length + existingDrafts.length;

    if (existingCount >= 10) {
      setImageError('이미지는 최대 10장까지 업로드할 수 있습니다.');
      return;
    }

    const remaining = Math.max(0, 10 - existingCount);
    const nextFiles = files.slice(0, remaining);

    setIsProcessingImages(true);
    setImageError('');
    try {
      const nextDrafts = [];
      for (const f of nextFiles) {
        const compressed = await compressImageToUnder1MB(f);
        const ext = getFileExt(compressed);
        const id = uuid();
        const name = `image.${ext}`;
        const file = new File([compressed], name, { type: compressed?.type || 'image/jpeg' });
        const previewUrl = URL.createObjectURL(file);
        nextDrafts.push({ id, file, previewUrl });
      }

      const mergedDrafts = [...existingDrafts, ...nextDrafts];

      const hasAnyCover = String(formData?.coverImageUrl || '').trim() || String(formData?.coverDraftId || '').trim();
      const nextCoverDraftId = hasAnyCover ? String(formData?.coverDraftId || '') : (mergedDrafts?.[0]?.id || '');

      onChange({
        ...(formData || {}),
        imageDrafts: mergedDrafts,
        coverDraftId: nextCoverDraftId
      });
    } catch (err) {
      setImageError(err?.message || '이미지 처리에 실패했습니다.');
    } finally {
      setIsProcessingImages(false);
    }
  };

  const handleRemoveUrlImage = (url) => {
    const list = Array.isArray(formData?.imageUrls) ? formData.imageUrls : [];
    const next = list.filter((u) => u !== url);
    const coverUrl = String(formData?.coverImageUrl || '').trim();
    const coverDraftId = String(formData?.coverDraftId || '').trim();

    const nextCoverUrl = coverUrl && coverUrl !== url ? coverUrl : '';
    const shouldPickAnotherCover = !nextCoverUrl && !coverDraftId;
    const fallbackCoverUrl = shouldPickAnotherCover ? (next[0] || '') : nextCoverUrl;

    onChange({
      ...(formData || {}),
      imageUrls: next,
      coverImageUrl: fallbackCoverUrl
    });
  };

  const handleRemoveDraftImage = (id) => {
    const drafts = Array.isArray(formData?.imageDrafts) ? formData.imageDrafts : [];
    const target = drafts.find((d) => d?.id === id);
    try {
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    } catch {
      // ignore
    }

    const nextDrafts = drafts.filter((d) => d?.id !== id);
    const coverDraftId = String(formData?.coverDraftId || '').trim();
    const coverUrl = String(formData?.coverImageUrl || '').trim();

    const nextCoverDraftId = coverDraftId && coverDraftId !== id ? coverDraftId : '';
    const hasAnyCover = coverUrl || nextCoverDraftId;
    const fallbackCoverDraftId = !hasAnyCover ? (nextDrafts?.[0]?.id || '') : nextCoverDraftId;

    onChange({
      ...(formData || {}),
      imageDrafts: nextDrafts,
      coverDraftId: fallbackCoverDraftId
    });
  };

  const handleSetCoverUrl = (url) => {
    onChange({
      ...(formData || {}),
      coverImageUrl: url,
      coverDraftId: ''
    });
  };

  const handleSetCoverDraft = (id) => {
    onChange({
      ...(formData || {}),
      coverDraftId: id,
      coverImageUrl: ''
    });
  };

  const handleOpenVenueSearch = () => {
    setVenueSearchQuery(String(formData?.venue || ''));
    setVenueSearchResults([]);
    setVenueSearchError('');
    setVenueSearchOpen(true);
  };

  const handleRunVenueSearch = async () => {
    if (typeof onVenueSearch !== 'function') {
      setVenueSearchError('검색 기능이 설정되어 있지 않습니다.');
      return;
    }

    const q = String(venueSearchQuery || '').trim();
    if (!q) {
      setVenueSearchError('검색어를 입력하세요');
      return;
    }

    setVenueSearchLoading(true);
    setVenueSearchError('');
    try {
      const results = await onVenueSearch(q);
      setVenueSearchResults(Array.isArray(results) ? results : []);
      if (!Array.isArray(results) || results.length === 0) {
        setVenueSearchError('검색 결과가 없습니다');
      }
    } catch (e) {
      setVenueSearchResults([]);
      setVenueSearchError(e?.message || '검색에 실패했습니다');
    } finally {
      setVenueSearchLoading(false);
    }
  };

  const handleSelectVenueResult = (row) => {
    const placeName = String(row?.placeName || '').trim();
    const address = String(row?.roadAddress || row?.jibunAddress || '').trim();
    const lat = row?.y != null ? String(row.y) : '';
    const lng = row?.x != null ? String(row.x) : '';

    onChange({
      ...(formData || {}),
      venue: placeName || String(formData?.venue || '').trim() || address,
      venueAddress: address,
      venueLat: lat,
      venueLng: lng
    });

    setVenueSearchOpen(false);
    setVenueSearchResults([]);
    setVenueSearchError('');
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <h4 className="font-heading font-medium text-foreground mb-4">기본 정보</h4>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">대표 이미지 / 사진</p>
                <p className="text-xs caption text-muted-foreground mt-1">1장 ~ 10장 업로드 (1MB 초과 시 자동 압축)</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleSelectImages}
                    disabled={isProcessingImages}
                    className="hidden"
                  />
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-muted/30 text-foreground cursor-pointer transition-athletic ${isProcessingImages ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted'}`}
                  >
                    <Icon name="Upload" size={18} />
                    <span className="text-sm font-medium">이미지 업로드</span>
                  </span>
                </label>
              </div>
            </div>

            {(errors?.coverImageUrl || imageError) && (
              <div className="mt-3 bg-error/10 border border-error/20 rounded-md p-3 flex items-start gap-2">
                <Icon name="AlertCircle" size={18} color="var(--color-error)" className="mt-0.5" />
                <p className="text-sm text-error">{errors?.coverImageUrl || imageError}</p>
              </div>
            )}

            {isProcessingImages && (
              <div className="mt-3 bg-muted rounded-md p-3 flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm caption text-muted-foreground">이미지 처리 중...</p>
              </div>
            )}

            {((Array.isArray(formData?.imageUrls) ? formData.imageUrls : []).length > 0 || (Array.isArray(formData?.imageDrafts) ? formData.imageDrafts : []).length > 0) && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {(formData.imageUrls || []).map((url) => {
                  const isCover = String(formData?.coverImageUrl || '').trim() === String(url || '').trim();
                  return (
                    <div key={url} className="border border-border rounded-lg overflow-hidden bg-card">
                      <div className="aspect-square bg-muted overflow-hidden">
                        <img src={url} alt="session" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2 space-y-2">
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            className={`text-xs px-2 py-1 rounded-md border transition-athletic ${isCover ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 border-border hover:bg-muted'}`}
                            onClick={() => handleSetCoverUrl(url)}
                          >
                            {isCover ? '대표' : '대표로'}
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic"
                            onClick={() => handleRemoveUrlImage(url)}
                            aria-label="삭제"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {(formData.imageDrafts || []).map((d) => {
                  const isCover = String(formData?.coverDraftId || '').trim() === String(d?.id || '').trim();
                  return (
                    <div key={d?.id} className="border border-border rounded-lg overflow-hidden bg-card">
                      <div className="aspect-square bg-muted overflow-hidden">
                        <img src={d?.previewUrl} alt="draft" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2 space-y-2">
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            className={`text-xs px-2 py-1 rounded-md border transition-athletic ${isCover ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 border-border hover:bg-muted'}`}
                            onClick={() => handleSetCoverDraft(d?.id)}
                          >
                            {isCover ? '대표' : '대표로'}
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic"
                            onClick={() => handleRemoveDraftImage(d?.id)}
                            aria-label="삭제"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <Input
              label="제목"
              type="text"
              placeholder="예: 주말 피클볼 오픈 게임"
              value={formData?.title || ''}
              onChange={(e) => handleChange('title', e?.target?.value)}
              error={errors?.title}
              required
              description="참가자들이 쉽게 알아볼 수 있는 제목을 입력하세요"
              className="bg-muted/30"
            />
          </div>

          <div className="space-y-2">
            <Input
              label="장소"
              type="text"
              placeholder="네이버 검색으로 장소를 선택하세요"
              value={formData?.venue || ''}
              readOnly
              onClick={handleOpenVenueSearch}
              onFocus={handleOpenVenueSearch}
              error={errors?.venue}
              required
              className="bg-muted/30"
            />

            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" iconName="MapPin" iconPosition="left" onClick={handleOpenVenueSearch}>
                네이버 검색
              </Button>
              {(formData?.venueAddress || formData?.venueLat || formData?.venueLng) && (
                <p className="text-xs caption text-muted-foreground">
                  {String(formData?.venueAddress || '').trim() ? String(formData?.venueAddress || '').trim() : '주소 미선택'}
                </p>
              )}
            </div>
          </div>

          <Select
            label="참가 레벨"
            options={levelOptions}
            value={formData?.skillLevel || ''}
            onChange={(value) => handleChange('skillLevel', value)}
            error={errors?.skillLevel}
            required
            placeholder="레벨을 선택하세요"
            description="참가 가능한 실력 수준"
            triggerClassName="bg-muted/30 text-foreground"
          />

          <Input
            label="코트 수"
            type="number"
            placeholder="예: 4"
            value={formData?.courtCount || ''}
            onChange={(e) => handleChange('courtCount', e?.target?.value)}
            error={errors?.courtCount}
            required
            min="1"
            max="20"
            description="사용 가능한 코트 개수"
            className="bg-muted/30"
          />

          <Input
            label="최대 참가 인원"
            type="number"
            placeholder="예: 16"
            value={formData?.maxParticipants || ''}
            onChange={(e) => handleChange('maxParticipants', e?.target?.value)}
            error={errors?.maxParticipants}
            required
            min="2"
            max="100"
            description="코트 수와 경기 형식을 고려하여 설정하세요"
            className="bg-muted/30"
          />

          <div className="lg:col-span-2">
            <Input
              label="설명"
              type="text"
              placeholder="추가 정보를 입력하세요"
              value={formData?.description || ''}
              onChange={(e) => handleChange('description', e?.target?.value)}
              error={errors?.description}
              description="참가자들에게 전달할 안내사항 (선택사항)"
              className="bg-muted/30"
            />
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <h4 className="font-heading font-medium text-foreground mb-4">일정</h4>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="시작일시"
            type="datetime-local"
            value={toDatetimeLocalValue(formData?.date, formData?.startTime)}
            onChange={(e) => {
              const { date, time } = splitDatetimeLocalValue(e?.target?.value);
              const hasEnd = Boolean(String(formData?.endDate || '').trim() && String(formData?.endTime || '').trim());
              const nextEnd = !hasEnd && date && time ? addHoursToLocalDatetime(date, time, 2) : null;

              onChange({
                ...(formData || {}),
                date,
                startTime: time,
                registrationDeadline: date && time ? `${date}T${time}` : '',
                ...(nextEnd?.date && nextEnd?.time
                  ? {
                      endDate: nextEnd.date,
                      endTime: nextEnd.time,
                    }
                  : {}),
              });
            }}
            error={errors?.date || errors?.startTime}
            required
            min={new Date()?.toISOString()?.slice(0, 16)}
            className="bg-muted/30"
          />

          <Input
            label="종료일시"
            type="datetime-local"
            value={toDatetimeLocalValue(formData?.endDate, formData?.endTime)}
            onChange={(e) => {
              const { date, time } = splitDatetimeLocalValue(e?.target?.value);
              onChange({
                ...(formData || {}),
                endDate: date,
                endTime: time,
              });
            }}
            error={errors?.endTime}
            min={toDatetimeLocalValue(formData?.date, formData?.startTime) || undefined}
            className="bg-muted/30"
          />

          <Input
            label="결제 마감 시간"
            type="datetime-local"
            value={formData?.paymentDeadline || ''}
            onChange={(e) => handleChange('paymentDeadline', e?.target?.value)}
            error={errors?.paymentDeadline}
            required
            min={new Date()?.toISOString()?.slice(0, 16)}
            description="참가비 입금 마감 시간"
            className="bg-muted/30"
          />
        </div>
      </div>
      {venueSearchOpen && (
        <div
          className="fixed inset-0 z-300 flex items-start justify-center pt-[10vh] px-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setVenueSearchOpen(false);
          }}
        >
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
          <div className="relative z-20 w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">장소 검색</h2>
                <p className="text-xs caption text-muted-foreground mt-0.5">주소를 검색하고 선택하세요</p>
              </div>
              <button
                type="button"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic"
                onClick={() => setVenueSearchOpen(false)}
                aria-label="닫기"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <input
                  className="flex-1 px-4 py-3 bg-muted/30 border border-border rounded-md text-foreground"
                  value={venueSearchQuery}
                  onChange={(e) => setVenueSearchQuery(e?.target?.value)}
                  placeholder="예: 서울 서대문구 이화여대길 52, 성남시 분당구 불정로 6"
                />
                <Button type="button" variant="default" onClick={handleRunVenueSearch} loading={venueSearchLoading}>
                  검색
                </Button>
              </div>

              {venueSearchError && (
                <div className="bg-error/10 border border-error/20 rounded-md p-3 flex items-start gap-2">
                  <Icon name="AlertCircle" size={18} color="var(--color-error)" className="mt-0.5" />
                  <p className="text-sm text-error">{venueSearchError}</p>
                </div>
              )}

              <div className="max-h-[60vh] overflow-y-auto divide-y divide-border border border-border rounded-lg">
                {(venueSearchResults || []).map((r, idx) => {
                  const placeName = String(r?.placeName || '').trim();
                  const address = String(r?.roadAddress || r?.jibunAddress || '').trim();
                  const sub = String(r?.jibunAddress || '').trim();
                  return (
                    <button
                      key={`${address}-${idx}`}
                      type="button"
                      className="w-full text-left p-4 hover:bg-muted transition-athletic"
                      onClick={() => handleSelectVenueResult(r)}
                    >
                      {placeName && <p className="text-sm font-semibold text-foreground">{placeName}</p>}
                      <p className="text-sm font-medium text-foreground">{address || '주소'}</p>
                      {sub && sub !== address && <p className="text-xs caption text-muted-foreground mt-1">{sub}</p>}
                      <p className="text-xs caption text-muted-foreground mt-1">위치: {r?.y}, {r?.x}</p>
                    </button>
                  );
                })}

                {Array.isArray(venueSearchResults) && venueSearchResults.length === 0 && !venueSearchLoading && !venueSearchError && (
                  <div className="p-4">
                    <p className="text-sm text-muted-foreground">주소 형태로 검색해보세요. (예: "서울 서대문구 이화여대길 52")</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicInfoStep;